class FrameUpsell extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.closest('[data-section]')?.dataset.section || '';
    this.dropdown = this.querySelector('[data-frame-dropdown]');
    this.dropdownButton = this.querySelector('[data-frame-dropdown-button]');
    this.dropdownList = this.querySelector('[data-frame-dropdown-list]');
    this.dropdownOptions = this.querySelectorAll('[data-frame-option]');
    this.currentSize = '';
    this.selectedFrameSku = null;
    this.isOpen = false;
    this.overlayElement = null;
    
    // Inramning elements
    this.inramningWrapper = this.querySelector('[data-inramning-wrapper]');
    this.inramningDropdown = this.querySelector('[data-inramning-dropdown]');
    this.inramningButton = this.querySelector('[data-inramning-dropdown-button]');
    this.inramningList = this.querySelector('[data-inramning-dropdown-list]');
    this.inramningOptions = this.querySelectorAll('[data-inramning-option]');
    this.selectedInramning = 'no';
    this.isInramningOpen = false;
    this.isCanvas = this.dataset.productType === 'canvas';
    
    // Price summary elements
    this.priceSummary = this.querySelector('[data-price-summary]');
    this.posterPriceElement = this.priceSummary?.querySelector('[data-poster-price]');
    this.framePriceElement = this.priceSummary?.querySelector('[data-frame-price]');
    this.framingPriceElement = this.priceSummary?.querySelector('[data-framing-price]');
    this.totalPriceElement = this.priceSummary?.querySelector('[data-total-price]');
    this.framePriceRow = this.priceSummary?.querySelector('[data-frame-price-row]');
    this.framingPriceRow = this.priceSummary?.querySelector('[data-framing-price-row]');
    
    // Price tracking
    this.posterPrice = 0;
    this.framePrice = 0;
    this.framingPrice = 0;
    
    this.init();
  }

  init() {
    // Get initial poster size
    this.updateCurrentSize();
    
    // Update frame availability
    this.updateFrameAvailability();
    
    // Setup dropdown interactions
    this.setupDropdown();
    
    // Setup inramning dropdown
    this.setupInramningDropdown();
    
    // Initialize price summary
    this.initPriceSummary();
    
    // Listen for variant changes on the main product
    this.observeVariantChanges();

    // Intercept add to cart to include frame
    this.interceptAddToCart();
  }

  setupDropdown() {
    if (!this.dropdownButton || !this.dropdownList) return;

    // Toggle dropdown on button click
    this.dropdownButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleDropdown();
    });

    // Handle option clicks
    this.dropdownOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        if (!option.classList.contains('frame-upsell__option--disabled')) {
          this.selectOption(option);
        }
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.dropdown.contains(e.target)) {
        this.closeDropdown();
      }
    });

    // Handle keyboard navigation
    this.dropdownButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleDropdown();
      } else if (e.key === 'Escape') {
        this.closeDropdown();
      }
    });
  }

  toggleDropdown() {
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    this.isOpen = true;
    this.dropdown.classList.add('frame-upsell__dropdown--open');
    this.dropdownButton.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    this.isOpen = false;
    this.dropdown.classList.remove('frame-upsell__dropdown--open');
    this.dropdownButton.setAttribute('aria-expanded', 'false');
  }

  selectOption(option) {
    const baseSku = option.dataset.frameSku;
    const name = option.dataset.frameName;
    const price = option.dataset.framePrice;
    const imageUrl = option.dataset.frameImage;

    // Update selected state
    this.dropdownOptions.forEach(opt => opt.classList.remove('frame-upsell__option--selected'));
    option.classList.add('frame-upsell__option--selected');

    // Update button display
    this.updateButtonDisplay(name, price, imageUrl, baseSku);

    // Store selected SKU
    this.selectedFrameSku = baseSku === 'none' ? null : baseSku;
    
    // Store frame price (get from variant ID stored in dataset)
    if (baseSku !== 'none') {
      const data = window.frameUpsellData?.[this.sectionId];
      const product = data?.products?.[baseSku];
      
      if (product && product.variants && product.variants.length > 0) {
        let variant = null;
        
        // Try to get the variant matching current size
        if (option.dataset.variantId) {
          variant = product.variants.find(v => v.id == option.dataset.variantId);
        }
        
        // Fallback to first available variant if no specific variant found
        if (!variant) {
          variant = product.variants.find(v => v.available) || product.variants[0];
        }
        
        this.framePrice = variant ? variant.price : 0;
      } else {
        this.framePrice = 0;
      }
    } else {
      this.framePrice = 0;
    }

    // Show/hide inramning dropdown based on frame selection
    this.toggleInramningVisibility();

    // Update frame overlay
    this.updateFrameOverlay(baseSku);
    
    // Update price summary with new frame price
    this.updatePriceSummary();

    // Close dropdown
    this.closeDropdown();
  }

  updateButtonDisplay(name, price, imageUrl, baseSku) {
    const buttonText = this.dropdownButton.querySelector('[data-frame-selected-text]');
    const buttonImage = this.dropdownButton.querySelector('[data-frame-selected-image]');
    const buttonPrice = this.dropdownButton.querySelector('[data-frame-selected-price]');

    if (buttonText) {
      buttonText.textContent = name;
    }

    if (buttonImage) {
      if (imageUrl && baseSku !== 'none') {
        buttonImage.src = imageUrl;
        buttonImage.style.display = 'block';
      } else {
        buttonImage.style.display = 'none';
      }
    }

    if (buttonPrice) {
      if (price && baseSku !== 'none') {
        buttonPrice.textContent = price;
        buttonPrice.style.display = 'block';
      } else {
        buttonPrice.style.display = 'none';
      }
    }
  }

  updateCurrentSize() {
    const data = window.frameUpsellData?.[this.sectionId];
    if (!data) return;
    
    const posterVariantTitle = data.currentPosterVariantTitle || '';
    this.currentSize = this.extractSize(posterVariantTitle);
    
    console.log('Initial poster variant:', { title: posterVariantTitle, size: this.currentSize });
  }

  extractSize(text) {
    // Extract size like "21x30", "30x40", "50x70", etc.
    // Matches pattern: number x number (with optional cm or spaces)
    // Works for both titles and SKUs
    const sizeMatch = text.match(/(\d+)\s*[xX×]\s*(\d+)/);
    if (sizeMatch) {
      // Normalize to lowercase 'x'
      return `${sizeMatch[1]}x${sizeMatch[2]}`;
    }
    return '';
  }

  observeVariantChanges() {
    // Listen for variant radio/select changes
    const variantRadios = document.querySelector(`#variant-radios-${this.sectionId}`);
    const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
    
    if (variantRadios) {
      variantRadios.addEventListener('change', (e) => {
        if (e.target.type === 'radio') {
          setTimeout(() => {
            this.onVariantChange();
          }, 100);
        }
      });
    }
    
    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        setTimeout(() => {
          this.onVariantChange();
        }, 100);
      });
    }

    // Also listen for hidden variant input changes (this is updated by Shopify's variant selector)
    const productForm = document.querySelector(`#product-form-${this.sectionId}`);
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        // Use MutationObserver to watch for value changes
        const observer = new MutationObserver(() => {
          this.onVariantChange();
        });
        
        observer.observe(variantInput, {
          attributes: true,
          attributeFilter: ['value']
        });

        // Also listen to change events
        variantInput.addEventListener('change', () => {
          this.onVariantChange();
        });
      }
    }
  }

  onVariantChange() {
    // Get the selected variant ID
    const productForm = document.querySelector(`#product-form-${this.sectionId}`);
    if (!productForm) return;
    
    const variantInput = productForm.querySelector('input[name="id"]');
    if (!variantInput) return;
    
    const variantId = variantInput.value;
    
    // Fetch variant details to get SKU
    this.fetchVariantSku(variantId);
  }

  async fetchVariantSku(variantId) {
    try {
      // Get the product JSON from the variant-radios or variant-selects script tag
      const variantRadios = document.querySelector(`#variant-radios-${this.sectionId}`);
      const variantSelects = document.querySelector(`#variant-selects-${this.sectionId}`);
      const container = variantRadios || variantSelects;
      
      if (container) {
        const scriptTag = container.querySelector('script[type="application/json"]');
        if (scriptTag) {
          const variants = JSON.parse(scriptTag.textContent);
          const variant = variants.find(v => v.id == variantId);
          
          if (variant) {
            // Extract size from variant title (e.g., "21x30", "30x40")
            const newSize = this.extractSize(variant.title || '');
            
            // Update poster price
            if (variant.price) {
              this.posterPrice = variant.price;
              // Also update in data object
              if (window.frameUpsellData?.[this.sectionId]) {
                window.frameUpsellData[this.sectionId].currentPosterPrice = variant.price;
              }
            }
            
            if (newSize && newSize !== this.currentSize) {
              this.currentSize = newSize;
              this.updateFrameAvailability();
              
              // Update data object
              if (window.frameUpsellData?.[this.sectionId]) {
                window.frameUpsellData[this.sectionId].currentPosterSku = variant.sku;
                window.frameUpsellData[this.sectionId].currentPosterVariantTitle = variant.title;
              }
              
              // Update frame price if a frame is already selected
              if (this.selectedFrameSku && this.selectedFrameSku !== 'none') {
                this.updateSelectedFramePrice();
              }
            }
            
            // Update price summary
            this.updatePriceSummary();
          }
        }
      }
    } catch (error) {
      console.error('Error fetching variant details:', error);
    }
  }

  updateFrameAvailability() {
    const data = window.frameUpsellData?.[this.sectionId];
    if (!data) return;
    
    if (!this.currentSize) {
      console.warn('No current size set for frame matching');
      return;
    }
    
    console.log('Updating frame availability for size:', this.currentSize);
    
    const frameOptions = this.querySelectorAll('[data-frame-option]');
    
    frameOptions.forEach(option => {
      const baseSku = option.dataset.frameSku;
      
      if (!baseSku || baseSku === 'none') return;
      
      const product = data.products[baseSku];
      if (!product) return;
      
      // Find variant matching current size
      // Support two SKU patterns:
      // 1. Standard: PREFIX-SIZE (e.g., DP-RAM-SVART-30x40)
      // 2. Canvas: BASE-SIZE-SUFFIX (e.g., FRAME-CANVAS-30x40-OAK)
      const variant = product.variants.find(v => {
        // First try matching by variant title (works for most cases)
        const variantSize = this.extractSize(v.title || '');
        if (variantSize === this.currentSize && v.available) {
          return true;
        }
        
        // For Canvas-style SKUs: match if SKU contains both the size and the baseSku parts
        // baseSku might be: "FRAME-CANVAS-OAK" or "FRAME-CANVAS-BLACK"
        // variant.sku might be: "FRAME-CANVAS-30X40-OAK" or "FRAME-CANVAS-30x40-OAK"
        if (v.sku && v.available) {
          const skuSize = this.extractSize(v.sku);
          
          // Remove size from both SKUs to compare the base parts (case-insensitive)
          const baseSkuParts = baseSku.toUpperCase().split('-').filter(part => !part.match(/^\d+[xX×]\d+$/i));
          const variantSkuParts = v.sku.toUpperCase().split('-').filter(part => !part.match(/^\d+[xX×]\d+$/i));
          
          // Check if size matches AND all base SKU parts are in variant SKU
          const sizeMatches = skuSize === this.currentSize;
          const baseMatches = baseSkuParts.every(part => variantSkuParts.includes(part));
          
          if (sizeMatches && baseMatches) {
            return true;
          }
        }
        
        return false;
      });
      
      console.log(`Frame ${baseSku}:`, variant ? `Found variant ${variant.sku} (ID: ${variant.id}) for size ${this.currentSize}` : `No match for size ${this.currentSize}`);
      
      if (variant) {
        option.dataset.variantId = variant.id;
        option.classList.remove('frame-upsell__option--disabled');
        
        // Update price display with the specific variant price
        const formattedPrice = this.formatMoney(variant.price);
        option.dataset.framePrice = formattedPrice;
        const priceElement = option.querySelector('[data-option-price]');
        if (priceElement) {
          priceElement.textContent = formattedPrice;
        }

        // Update button display if this option is currently selected
        if (option.classList.contains('frame-upsell__option--selected')) {
          const buttonPrice = this.dropdownButton?.querySelector('[data-frame-selected-price]');
          if (buttonPrice) {
            buttonPrice.textContent = formattedPrice;
          }
        }
      } else {
        option.dataset.variantId = '';
        option.classList.add('frame-upsell__option--disabled');
        
        // If this was selected, switch to "no frame"
        if (option.classList.contains('frame-upsell__option--selected')) {
          const noFrameOption = this.querySelector('[data-frame-option][data-frame-sku="none"]');
          if (noFrameOption) {
            this.selectOption(noFrameOption);
          }
          this.selectedFrameSku = null;
        }
        
        // Update price to show unavailable
        const priceElement = option.querySelector('[data-option-price]');
        if (priceElement) {
          priceElement.textContent = 'Ej tillgänglig';
        }
      }
    });
  }

  updateSelectedFramePrice() {
    // Update the price of the currently selected frame based on the new size
    if (!this.selectedFrameSku) return;
    
    const data = window.frameUpsellData?.[this.sectionId];
    const product = data?.products?.[this.selectedFrameSku];
    
    if (product && product.variants && product.variants.length > 0) {
      // Find the selected frame option to get its variantId
      const selectedOption = this.querySelector(`[data-frame-option][data-frame-sku="${this.selectedFrameSku}"].frame-upsell__option--selected`);
      
      if (selectedOption && selectedOption.dataset.variantId) {
        const variant = product.variants.find(v => v.id == selectedOption.dataset.variantId);
        if (variant) {
          this.framePrice = variant.price;
        }
      }
    }
  }

  formatMoney(cents) {
    // Format like Shopify money_without_trailing_zeros filter
    const amount = cents / 100;
    
    // Remove trailing zeros
    let formatted = amount.toFixed(2).replace(/\.?0+$/, '');
    
    // Replace decimal point with comma (Swedish format)
    formatted = formatted.replace('.', ',');
    
    return `${formatted} kr`;
  }

  updateFrameOverlay(frameSku) {
    const data = window.frameUpsellData?.[this.sectionId];
    if (!data) return;

    // If "no frame" is selected, remove overlay
    if (frameSku === 'none' || !frameSku) {
      this.removeFrameOverlay();
      return;
    }

    // Get frame product data
    const frameProduct = data.products[frameSku];
    if (!frameProduct) {
      console.log('Frame product not found:', frameSku);
      this.removeFrameOverlay();
      return;
    }

    // Choose the correct overlay image based on product orientation
    const overlayImage = data.isLandscape 
      ? (frameProduct.overlayImageLandscape || frameProduct.overlayImage)
      : frameProduct.overlayImage;

    if (!overlayImage) {
      console.log('No overlay image found for frame:', frameSku, 'isLandscape:', data.isLandscape);
      this.removeFrameOverlay();
      return;
    }

    // Show or update overlay
    this.showFrameOverlay(overlayImage);
  }

  showFrameOverlay(imageUrl) {
    // Find the first product image
    const firstImage = this.findFirstProductImage();
    if (!firstImage) {
      console.warn('Could not find first product image');
      return;
    }

    // Create overlay element if it doesn't exist
    if (!this.overlayElement) {
      this.overlayElement = document.createElement('div');
      this.overlayElement.className = 'frame-overlay';
      this.overlayElement.innerHTML = `
        <img class="frame-overlay__image" src="${imageUrl}" alt="Frame overlay" />
      `;
      
      // Add to the first image container
      const container = firstImage.querySelector('.product__media') || firstImage;
      container.style.position = 'relative';
      container.appendChild(this.overlayElement);
      
      // Trigger fade in
      setTimeout(() => {
        this.overlayElement.classList.add('frame-overlay--visible');
      }, 10);
    } else {
      // Update existing overlay image
      const img = this.overlayElement.querySelector('.frame-overlay__image');
      if (img && img.src !== imageUrl) {
        // Fade out
        this.overlayElement.classList.remove('frame-overlay--visible');
        
        // Wait for fade out, then change image and fade in
        setTimeout(() => {
          img.src = imageUrl;
          setTimeout(() => {
            this.overlayElement.classList.add('frame-overlay--visible');
          }, 10);
        }, 300);
      }
    }
  }

  removeFrameOverlay() {
    if (this.overlayElement) {
      // Fade out
      this.overlayElement.classList.remove('frame-overlay--visible');
      
      // Remove after transition
      setTimeout(() => {
        if (this.overlayElement && this.overlayElement.parentNode) {
          this.overlayElement.parentNode.removeChild(this.overlayElement);
        }
        this.overlayElement = null;
      }, 300);
    }
  }

  findFirstProductImage() {
    // Try to find the first product media item
    // Works for both stacked gallery and slider layouts
    const mediaList = document.querySelector(`[data-section="main-${this.sectionId}"] .product__media-list`);
    if (!mediaList) return null;

    // Find the first media item (handles both stacked and swiper layouts)
    const firstMediaItem = mediaList.querySelector('.product__media-item');
    return firstMediaItem;
  }

  setupInramningDropdown() {
    if (!this.inramningButton || !this.inramningList) return;

    // Toggle dropdown on button click
    this.inramningButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleInramningDropdown();
    });

    // Handle option clicks
    this.inramningOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        this.selectInramningOption(option);
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.inramningDropdown && !this.inramningDropdown.contains(e.target)) {
        this.closeInramningDropdown();
      }
    });

    // Handle keyboard navigation
    this.inramningButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleInramningDropdown();
      } else if (e.key === 'Escape') {
        this.closeInramningDropdown();
      }
    });
  }

  toggleInramningDropdown() {
    if (this.isInramningOpen) {
      this.closeInramningDropdown();
    } else {
      this.openInramningDropdown();
    }
  }

  openInramningDropdown() {
    this.isInramningOpen = true;
    this.inramningDropdown.classList.add('frame-upsell__dropdown--open');
    this.inramningButton.setAttribute('aria-expanded', 'true');
  }

  closeInramningDropdown() {
    this.isInramningOpen = false;
    this.inramningDropdown.classList.remove('frame-upsell__dropdown--open');
    this.inramningButton.setAttribute('aria-expanded', 'false');
  }

  selectInramningOption(option) {
    const value = option.dataset.inramningValue;
    const name = option.dataset.inramningName;
    const price = option.dataset.inramningPrice;
    const variantId = option.dataset.inramningVariantId;

    // Update selected state
    this.inramningOptions.forEach(opt => opt.classList.remove('frame-upsell__option--selected'));
    option.classList.add('frame-upsell__option--selected');

    // Update button display
    this.updateInramningButtonDisplay(name, price);

    // Store selected value
    this.selectedInramning = value;
    
    // Store framing price
    if (value === 'yes') {
      // Get inramning product data from window.frameUpsellData
      const data = window.frameUpsellData?.[this.sectionId];
      const inramningProduct = data?.inramningProduct;
      if (inramningProduct && inramningProduct.price) {
        this.framingPrice = inramningProduct.price;
      } else {
        this.framingPrice = 0;
      }
    } else {
      this.framingPrice = 0;
    }
    
    // Update price summary
    this.updatePriceSummary();

    // Close dropdown
    this.closeInramningDropdown();
  }

  updateInramningButtonDisplay(name, price) {
    const buttonText = this.inramningButton?.querySelector('[data-inramning-selected-text]');
    const buttonPrice = this.inramningButton?.querySelector('[data-inramning-selected-price]');

    if (buttonText) {
      buttonText.textContent = name;
    }

    if (buttonPrice) {
      if (price) {
        buttonPrice.textContent = price;
        buttonPrice.style.display = 'block';
      } else {
        buttonPrice.style.display = 'none';
      }
    }
  }

  toggleInramningVisibility() {
    const hasFrame = this.selectedFrameSku && this.selectedFrameSku !== 'none';

    // For canvas: no inramning dropdown, just update price summary
    if (this.isCanvas) {
      this.updatePriceSummary();
      return;
    }

    if (!this.inramningWrapper) return;

    // Show inramning dropdown when a frame is selected, hide when "no frame" is selected
    if (hasFrame) {
      this.inramningWrapper.style.display = 'block';
    } else {
      this.inramningWrapper.style.display = 'none';
      // Reset to "No" when hiding
      const noOption = this.querySelector('[data-inramning-option][data-inramning-value="no"]');
      if (noOption) {
        this.selectInramningOption(noOption);
      }
    }

    // Update price summary when inramning visibility changes
    this.updatePriceSummary();
  }

  initPriceSummary() {
    // Get initial poster price from frameUpsellData
    const data = window.frameUpsellData?.[this.sectionId];
    if (data && data.currentPosterPrice) {
      this.posterPrice = data.currentPosterPrice;
    }
    
    this.updatePriceSummary();
  }

  updatePriceSummary() {
    if (!this.priceSummary) return;
    
    // Check if frame or framing is selected
    const hasFrame = this.selectedFrameSku && this.selectedFrameSku !== 'none';
    const hasFraming = hasFrame && this.selectedInramning === 'yes';
    
    // Show/hide price summary
    if (hasFrame || hasFraming) {
      this.priceSummary.style.display = 'block';
    } else {
      this.priceSummary.style.display = 'none';
      return;
    }
    
    // Update poster price
    if (this.posterPriceElement) {
      this.posterPriceElement.textContent = this.formatMoney(this.posterPrice);
    }
    
    // Update frame price row
    if (hasFrame && this.framePriceRow) {
      this.framePriceRow.style.display = 'flex';
      if (this.framePriceElement) {
        this.framePriceElement.textContent = this.formatMoney(this.framePrice);
      }
    } else if (this.framePriceRow) {
      this.framePriceRow.style.display = 'none';
    }
    
    // Update framing price row
    if (hasFraming && this.framingPriceRow) {
      this.framingPriceRow.style.display = 'flex';
      if (this.framingPriceElement) {
        this.framingPriceElement.textContent = this.formatMoney(this.framingPrice);
      }
    } else if (this.framingPriceRow) {
      this.framingPriceRow.style.display = 'none';
    }
    
    // Calculate and update total price
    const totalPrice = this.posterPrice + (hasFrame ? this.framePrice : 0) + (hasFraming ? this.framingPrice : 0);
    if (this.totalPriceElement) {
      this.totalPriceElement.textContent = this.formatMoney(totalPrice);
    }
  }

  interceptAddToCart() {
    // Find the product form
    const productForm = document.querySelector(`#product-form-${this.sectionId}`);
    if (!productForm) {
      console.error('Product form not found:', `#product-form-${this.sectionId}`);
      return;
    }

    // Store reference to this instance
    const frameUpsell = this;

    // Capture form submission at the earliest possible phase
    productForm.addEventListener('submit', async function(e) {
      // Check if a frame or inramning is selected
      const hasFrame = frameUpsell.selectedFrameSku && frameUpsell.selectedFrameSku !== 'none';
      const hasInramning = hasFrame && frameUpsell.selectedInramning === 'yes';
      
      if (!hasFrame && !hasInramning) {
        // No frame or inramning selected, let normal flow continue
        return;
      }

      // Frame or inramning is selected - we MUST handle this ourselves
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Get current form data
      const formData = new FormData(this);
      const posterId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || '1');

      console.log('Intercepted submit - Poster ID:', posterId, 'Quantity:', quantity, 'Has Frame:', hasFrame, 'Has Inramning:', hasInramning);

      let frameVariantId = null;
      if (hasFrame) {
        // Get frame variant ID from the selected option
        const frameOption = frameUpsell.querySelector(`[data-frame-option][data-frame-sku="${frameUpsell.selectedFrameSku}"]`);
        frameVariantId = frameOption?.dataset.variantId;

        if (!frameVariantId) {
          alert('Den valda ramen är inte tillgänglig för denna storlek.');
          return;
        }
      }

      let inramningVariantId = null;
      if (hasInramning) {
        // Get inramning variant ID from the dropdown option
        const inramningOption = frameUpsell.querySelector('[data-inramning-option][data-inramning-value="yes"]');
        inramningVariantId = inramningOption?.dataset.inramningVariantId;

        if (!inramningVariantId) {
          alert('Inramning är inte tillgänglig just nu.');
          return;
        }
      }

      console.log('Frame Variant ID:', frameVariantId, 'Inramning Variant ID:', inramningVariantId);

      // Add all items to cart
      await frameUpsell.addItemsToCart(posterId, frameVariantId, inramningVariantId, quantity);
      
      return false;
    }, true); // Use capture phase to intercept BEFORE other handlers
  }

  async addItemsToCart(posterId, frameId, inramningId, quantity = 1) {
    console.log('Adding to cart:', { posterId, frameId, inramningId, quantity });

    // Show loading on the submit button
    const submitButton = document.querySelector(`#product-form-${this.sectionId} [name="add"]`);
    const buttonText = submitButton?.querySelector('span');
    const spinner = submitButton?.querySelector('.loading-overlay__spinner');
    
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
    }
    if (spinner) spinner.classList.remove('hidden');

    try {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      
      // Generate bundle ID when framing is included OR for canvas products (frame includes framing)
      const isCanvas = this.isCanvas;
      const bundleId = (inramningId || isCanvas) ? `b${Date.now().toString(36)}` : null;

      // Build items array - bundle properties only when framing is present
      const items = [
        bundleId
          ? { id: parseInt(posterId), quantity: parseInt(quantity), properties: { '_bundle_id': bundleId, '_bundle_role': 'poster' } }
          : { id: parseInt(posterId), quantity: parseInt(quantity) }
      ];

      if (frameId) {
        items.push(bundleId
          ? { id: parseInt(frameId), quantity: parseInt(quantity), properties: { '_bundle_id': bundleId, '_bundle_role': 'frame' } }
          : { id: parseInt(frameId), quantity: parseInt(quantity) }
        );
      }

      if (inramningId) {
        items.push({ id: parseInt(inramningId), quantity: parseInt(quantity), properties: { '_bundle_id': bundleId, '_bundle_role': 'framing' } });
      }
      
      // Build request body with items and sections
      const requestBody = {
        items: items
      };

      // Add sections to render (for cart drawer refresh)
      if (cart) {
        const sectionsToRender = cart.getSectionsToRender().map(section => section.id);
        requestBody.sections = sectionsToRender.join(',');
        requestBody.sections_url = window.location.pathname;
        cart.setActiveElement(document.activeElement);
      }

      console.log('Sending request:', requestBody);

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody),
        redirect: 'manual'  // Don't follow redirects automatically
      });

      console.log('Response status:', response.status, response.statusText);

      // Handle redirect (302) - means it's trying to redirect to cart page
      if (response.status === 302 || response.status === 0) {
        // Items were added successfully but response is a redirect
        // We need to fetch the cart data manually
        const cartResponse = await fetch('/cart.js', {
          headers: {
            'Accept': 'application/json'
          }
        });
        const cartData = await cartResponse.json();
        console.log('Fetched cart data after redirect:', cartData);
        
        // Create a response object that matches what renderContents expects
        const responseData = {
          items: cartData.items,
          item_count: cartData.item_count,
          sections: {}
        };
        
        // Fetch sections if needed
        if (cart && requestBody.sections) {
          const sectionsToFetch = requestBody.sections.split(',');
          for (const sectionId of sectionsToFetch) {
            try {
              const sectionResponse = await fetch(`${requestBody.sections_url}?sections=${sectionId}`);
              const sectionData = await sectionResponse.json();
              if (sectionData[sectionId]) {
                responseData.sections[sectionId] = sectionData[sectionId];
              }
            } catch (err) {
              console.warn('Failed to fetch section:', sectionId, err);
            }
          }
        }
        
        // Publish cart update event
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'frame-upsell',
            productVariantId: posterId
          });
        }

        // Render cart contents with the fetched data
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(responseData);
        }

        // Open cart drawer
        if (cart && typeof cart.open === 'function') {
          cart.open();
        }

        // Remove is-empty class if present
        if (cart && cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }

        // Show success
        this.showSuccessMessage();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ description: 'Unknown error' }));
        console.error('Cart API error:', errorData);
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const responseData = await response.json();
      console.log('Cart response:', responseData);

      if (responseData.status) {
        // Handle error response
        throw new Error(responseData.description || responseData.message);
      }

      // Publish cart update event (like product-form.js does)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'frame-upsell',
          productVariantId: posterId
        });
      }

      // Render cart contents with the response data
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(responseData);
      }

      // Open cart drawer
      if (cart && typeof cart.open === 'function') {
        cart.open();
      }

      // Remove is-empty class if present
      if (cart && cart.classList.contains('is-empty')) {
        cart.classList.remove('is-empty');
      }

      // Show success
      this.showSuccessMessage();

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Det gick inte att lägga till produkterna i varukorgen. Försök igen.');
    } finally {
      if (submitButton) {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      }
      if (spinner) spinner.classList.add('hidden');
    }
  }

  showSuccessMessage() {
    const message = document.createElement('div');
    message.className = 'frame-upsell-success';
    
    // Build message based on what was added
    let messageText = 'Tillagd i varukorgen!';
    if (this.selectedFrameSku && this.selectedInramning === 'yes') {
      messageText = 'Poster, ram och inramning tillagda!';
    } else if (this.selectedFrameSku) {
      messageText = 'Poster och ram tillagda!';
    }
    
    message.textContent = messageText;
    message.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #4CAF50; color: white; padding: 16px 24px; border-radius: 4px; z-index: 10000; box-shadow: 0 2px 8px rgba(0,0,0,0.2);';
    
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.transition = 'opacity 0.3s';
      message.style.opacity = '0';
      setTimeout(() => {
        if (document.body.contains(message)) {
          document.body.removeChild(message);
        }
      }, 300);
    }, 3000);
  }
}

// Define custom element
if (!customElements.get('frame-upsell')) {
  customElements.define('frame-upsell', FrameUpsell);
}

